import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { SpheraExportParams, SpheraOptionType } from '@/types'
import {
	createSpheraOption,
	deleteSpheraOption,
	downloadSpheraExport,
	getSpheraBranchConfigs,
	getSpheraCrConfigs,
	getSpheraOptions,
	getSpheraPreview,
	getSpheraSettings,
	renameSpheraOption,
	updateBranchSpheraLocation,
	updateCrSpheraService,
	updateSpheraSettings,
} from '../services/sphera-export'

export const spheraExportKeys = {
	options: ['sphera-export', 'options'] as const,
	preview: (params: SpheraExportParams) =>
		['sphera-export', 'preview', params] as const,
	allPreviews: ['sphera-export', 'preview'] as const,
	branches: ['sphera-export', 'branches'] as const,
	crs: ['sphera-export', 'crs'] as const,
	settings: ['sphera-export', 'settings'] as const,
}

export function useSpheraOptions() {
	return useQuery({
		queryKey: spheraExportKeys.options,
		queryFn: getSpheraOptions,
	})
}

export function useSpheraBranchConfigs() {
	return useQuery({
		queryKey: spheraExportKeys.branches,
		queryFn: getSpheraBranchConfigs,
	})
}

export function useSpheraCrConfigs() {
	return useQuery({
		queryKey: spheraExportKeys.crs,
		queryFn: getSpheraCrConfigs,
	})
}

export function useSpheraPreview(params: SpheraExportParams) {
	return useQuery({
		queryKey: spheraExportKeys.preview(params),
		queryFn: () => getSpheraPreview(params),
		enabled: params.types.length > 0,
	})
}

export function useDownloadSpheraExport() {
	return useMutation({ mutationFn: downloadSpheraExport })
}

export function useUpdateCrSpheraService() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({
			id,
			spheraService,
		}: {
			id: string
			spheraService: string | null
		}) => updateCrSpheraService(id, spheraService),
		onSuccess: () =>
			Promise.all([
				queryClient.invalidateQueries({
					queryKey: spheraExportKeys.allPreviews,
				}),
				queryClient.invalidateQueries({ queryKey: spheraExportKeys.crs }),
				queryClient.invalidateQueries({ queryKey: spheraExportKeys.options }),
			]),
	})
}

export function useUpdateBranchSpheraLocation() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({
			id,
			spheraLocation,
		}: {
			id: string
			spheraLocation: string | null
		}) => updateBranchSpheraLocation(id, spheraLocation),
		onSuccess: () =>
			Promise.all([
				queryClient.invalidateQueries({
					queryKey: spheraExportKeys.allPreviews,
				}),
				queryClient.invalidateQueries({ queryKey: spheraExportKeys.branches }),
			]),
	})
}

// Mudar os valores do Sphera afeta cadastros (flags/serviços) e a prévia da exportação
function useInvalidateSpheraOptions() {
	const queryClient = useQueryClient()
	return () =>
		Promise.all([
			queryClient.invalidateQueries({ queryKey: spheraExportKeys.options }),
			queryClient.invalidateQueries({ queryKey: spheraExportKeys.allPreviews }),
			queryClient.invalidateQueries({ queryKey: spheraExportKeys.crs }),
			queryClient.invalidateQueries({ queryKey: ['catalog'] }),
		])
}

export function useCreateSpheraOption() {
	const invalidate = useInvalidateSpheraOptions()
	return useMutation({
		mutationFn: ({ type, name }: { type: SpheraOptionType; name: string }) =>
			createSpheraOption(type, name),
		onSuccess: invalidate,
	})
}

export function useRenameSpheraOption() {
	const invalidate = useInvalidateSpheraOptions()
	return useMutation({
		mutationFn: ({ id, name }: { id: string; name: string }) =>
			renameSpheraOption(id, name),
		onSuccess: invalidate,
	})
}

export function useDeleteSpheraOption() {
	const invalidate = useInvalidateSpheraOptions()
	return useMutation({
		mutationFn: (id: string) => deleteSpheraOption(id),
		onSuccess: invalidate,
	})
}

export function useSpheraSettings() {
	return useQuery({
		queryKey: spheraExportKeys.settings,
		queryFn: getSpheraSettings,
	})
}

export function useUpdateSpheraSettings() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: updateSpheraSettings,
		onSuccess: () =>
			Promise.all([
				queryClient.invalidateQueries({ queryKey: spheraExportKeys.settings }),
				queryClient.invalidateQueries({
					queryKey: spheraExportKeys.allPreviews,
				}),
			]),
	})
}
